import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { X } from "lucide-react";
import { ZONES, regionsWithArtwork } from "../data/regions";

export default function ViewAll({ onSelect, onClose }) {
  const [zone, setZone] = useState("ALL");
  const scrollRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ wrapper: scrollRef.current, content: scrollRef.current.firstElementChild, lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const all = regionsWithArtwork();
  const items = zone === "ALL" ? all : all.filter((r) => r.zone === zone);
  const usedZones = ZONES.filter((z) => all.some((r) => r.zone === z));

  return (
    <motion.div
      data-testid="view-all"
      className="fixed inset-0 z-[70] archival-vignette grain flex flex-col"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="px-6 sm:px-12 pt-8 pb-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-nav text-[9px] tracking-[0.45em] uppercase text-[#857565]">The Complete Index</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[0.12em] uppercase text-[#E8DCC4] mt-2">
            View All
          </h2>
        </div>
        <button
          data-testid="view-all-close"
          onClick={onClose}
          aria-label="Close index"
          className="text-[#857565] hover:text-[#C5A059] transition-colors duration-500"
        >
          <X size={20} strokeWidth={1.25} />
        </button>
      </div>

      <div className="px-6 sm:px-12 pb-6 flex flex-wrap gap-2" data-testid="zone-filters">
        {["ALL", ...usedZones].map((z) => (
          <button
            key={z}
            data-testid={`filter-${z.toLowerCase()}`}
            onClick={() => setZone(z)}
            className={`font-nav text-[9px] tracking-[0.35em] uppercase px-4 py-2 border transition-colors duration-500 ${
              zone === z
                ? "border-[rgba(197,160,89,0.55)] text-[#C5A059]"
                : "border-[rgba(197,160,89,0.15)] text-[#857565] hover:text-[#B8A795]"
            }`}
          >
            {z}
          </button>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto archival-scroll px-6 sm:px-12 pb-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14 max-w-6xl mx-auto">
          {items.map((r, i) => (
            <motion.button
              key={r.id}
              data-testid={`index-card-${r.id}`}
              onClick={() => onSelect(r.id)}
              initial={{ opacity: 0, y: 34 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 * i, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="index-card group text-left"
            >
              <div className="manuscript-frame overflow-hidden" style={{ aspectRatio: "3/2" }}>
                <img
                  src={r.images[0]}
                  alt={`${r.name} artwork`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex items-baseline gap-3 mt-4">
                <span className="font-nav text-[9px] tracking-[0.2em] text-[#6f6252]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="font-display text-lg tracking-[0.1em] uppercase text-[#D6C5B3] group-hover:text-[#C5A059] transition-colors duration-500 truncate">
                    {r.name}
                  </div>
                  <div className="font-nav text-[9px] tracking-[0.3em] uppercase text-[#857565] mt-1">
                    {r.zone} Bharat{r.images.length > 1 ? ` · ${r.images.length} plates` : ""}
                  </div>
                  <div className="card-line h-px bg-[#C5A059] mt-2 w-16" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
